
import { Component, Watch, Vue } from "vue-property-decorator";
import { TimesheetList } from "./TimesheetList.vue";
import {
  TimesheetFilterQueryPayload,
  TimesheetAllPayload,
  Timesheet,
  Technician,
  MR,
} from "@/store/modules";
import { CDataTableSortVal } from "@/@types";
import { queryFilterHasChange, filterEmptyQuery } from "@/utility";
import { SortQueryPayload } from "@/store/modules";
import TechnicianMultiDropdown from "@/pages/technician/filters/TechnicianMultiDropdown.vue";
import MRMultiDropdown from "@/pages/mr/filters/MRMultiDropdown.vue";
import TimesheetListHandler from './TimesheetListHandler.vue';

@Component({
  components: { TimesheetList, TechnicianMultiDropdown, MRMultiDropdown, TimesheetListHandler },
})
export default class TimesheetListPage extends Vue {
  limit = 25;

  colFilter: TimesheetFilterQueryPayload = {
    startDate: "",
    endDate: "",
  };

  currentPage = 1;

  sort: SortQueryPayload = {
    sort: "ID",
    order: "desc",
  };

  sortVal: CDataTableSortVal = {};

  // @Model("tech-select", { type: Array, default: () => [] })
  filterTechs!: Technician[];

  filterMR!: MR[];

  debounceTimeout?: NodeJS.Timeout;

  /**
   * in ms
   */
  debounceTime = 1000;

  @Watch("colFilter", { deep: true })
  onColFilterChange(
    val: TimesheetFilterQueryPayload | null,
    oldVal: TimesheetFilterQueryPayload | null
  ) {
    if (!val || !oldVal) {
      return;
    }
    console.log(
      "p",
      val,
      oldVal,
      queryFilterHasChange<TimesheetFilterQueryPayload>(val, oldVal)
    );
    if (queryFilterHasChange<TimesheetFilterQueryPayload>(val, oldVal)) {
      this.load();
    }
  }

  @Watch("limit")
  onLimitChange(val: number, oldVal: number) {
    if (val !== oldVal) {
      this.load();
    }
  }

  @Watch("sortVal")
  onSortValChange(val: CDataTableSortVal, oldVal: CDataTableSortVal) {
    this.sort = {
      sort: val.column || "",
      order: val.asc ? "asc" : "desc",
    };
    this.load();
  }

  mounted() {
    this.load();
  }

  load(debounce = false) {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
    }

    if (!debounce) {
      this.$nextTick(() => this.fetchRender(this.compilePayload()));
    }

    this.debounceTimeout = setTimeout(
      () => this.$nextTick(() => this.fetchRender(this.compilePayload())),
      this.debounceTime
    );
  }

  async fetchRender(payload: TimesheetAllPayload) {
    await Timesheet.dispatch("all", payload);
  }

  get items(): Array<any> {
    const data = Timesheet.query().withAll().get();
    return data;
  }

  get loading(): boolean {
    return !!Timesheet.store().state.entities.timesheet.fetching;
  }

  get total() {
    return Timesheet.store().state.entities.timesheet.total;
  }

  get numPages(): number {
    const total = Timesheet.store().state.entities.timesheet.total;

    if (!total) {
      return 0;
    }
    // console.log('t', Math.ceil(total / this.limit), 1);
    return Math.max(Math.ceil(total / this.limit), 1);
  }

  get totalHours(): number {
    return Timesheet.store().state.entities.timesheet.hours;
  }

  onPageChange(page: number) {
    this.$nextTick(() =>
      this.fetchRender(
        this.compilePayload({
          offset: Math.floor(this.limit * (page - 1)),
        })
      )
    );
  }

  compilePayload(
    merge: Partial<TimesheetAllPayload> = {}
  ): TimesheetAllPayload {
    return {
      limit: this.limit,
      offset: Math.floor(this.limit * (this.currentPage - 1)),
      ...this.sort,
      ...{ filter: this.compileFilter() },
      ...merge,
    };
  }

  compileFilter(): TimesheetAllPayload["filter"] {
    const f = filterEmptyQuery(this.colFilter);
    const timeRange = [];
    if (this.colFilter.startDate || this.colFilter.endDate) {
      timeRange.push(this.colFilter.startDate || "");
      timeRange.push(
        this.colFilter.endDate || new Date(Date.now()).toISOString()
      );
      delete f.startDate;
      delete f.endDate;
    }

    if (timeRange.length) {
      f.timeRange = timeRange.join(",");
    }

    if (this.filterTechs && this.filterTechs.length) {
      f.techID = (this.filterTechs || []).map((tech) => tech.ID).join(",");
    }

    if (this.filterMR && this.filterMR.length) {
      f.mrID = (this.filterMR || []).map((mr) => mr.id).join(",");
    }

    f.voided = 0;

    return { ...f };
  }

  onTechSelect(options: Technician[]) {
    this.filterTechs = options;

    this.load(true);
  }

  onMR(options: MR[]) {
    this.filterMR = options;
    this.load(true);
  }
}
